import { useState } from 'react';
import { FaGamepad, FaUsers, FaEnvelope, FaBriefcase, FaImages, FaTimes, FaBars } from 'react-icons/fa';
import { useLanguage } from '../i18n';

const Navigation = ({ activeSection, setActiveSection }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const labels = { projects: t('projects'), portfolio: t('portfolio'), services: t('services'), members: t('members'), contact: t('contact') };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavClick = (section) => {
    setActiveSection(section);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 w-full bg-[#11051d]/75 backdrop-blur-xl z-50 border-b border-white/10 shadow-lg shadow-black/20">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div 
              className="flex items-center space-x-2 sm:space-x-3 cursor-pointer" 
              onClick={() => handleNavClick('home')}
            >
              <img src="/nuclearLogo.png" alt="Nuclear Team Logo" className="h-8 w-8 sm:h-10 sm:w-10 shadow-md shadow-purple-500/25" />
              <h1 className="text-lg sm:text-2xl font-black text-white tracking-normal">NUCLEAR TEAM</h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-4 lg:space-x-8">
              <button 
                onClick={() => setActiveSection('projects')}
                className={`flex items-center space-x-1 lg:space-x-2 rounded-full px-3 py-2 text-sm lg:text-base transition-all duration-300 ${
                  activeSection === 'projects' ? 'bg-white/10 text-purple-200' : 'text-white/85 hover:bg-white/10 hover:text-purple-200'
                }`}
              >
                <FaGamepad className="text-sm lg:text-base" />
                <span>{labels.projects}</span>
              </button>
              <button 
                onClick={() => setActiveSection('portfolio')}
                className={`flex items-center space-x-1 lg:space-x-2 rounded-full px-3 py-2 text-sm lg:text-base transition-all duration-300 ${
                  activeSection === 'portfolio' ? 'bg-white/10 text-purple-200' : 'text-white/85 hover:bg-white/10 hover:text-purple-200'
                }`}
              >
                <FaImages className="text-sm lg:text-base" />
                <span>{labels.portfolio}</span>
              </button>
              <button 
                onClick={() => setActiveSection('services')}
                className={`flex items-center space-x-1 lg:space-x-2 rounded-full px-3 py-2 text-sm lg:text-base transition-all duration-300 ${
                  activeSection === 'services' ? 'bg-white/10 text-purple-200' : 'text-white/85 hover:bg-white/10 hover:text-purple-200'
                }`}
              >
                <FaBriefcase className="text-sm lg:text-base" />
                <span>{labels.services}</span>
              </button>
              <button 
                onClick={() => setActiveSection('members')}
                className={`flex items-center space-x-1 lg:space-x-2 rounded-full px-3 py-2 text-sm lg:text-base transition-all duration-300 ${
                  activeSection === 'members' ? 'bg-white/10 text-purple-200' : 'text-white/85 hover:bg-white/10 hover:text-purple-200'
                }`}
              >
                <FaUsers className="text-sm lg:text-base" />
                <span>{labels.members}</span>
              </button>
              <button 
                onClick={() => setActiveSection('contact')}
                className={`flex items-center space-x-1 lg:space-x-2 rounded-full px-3 py-2 text-sm lg:text-base transition-all duration-300 ${
                  activeSection === 'contact' ? 'bg-white/10 text-purple-200' : 'text-white/85 hover:bg-white/10 hover:text-purple-200'
                }`}
              >
                <FaEnvelope className="text-sm lg:text-base" />
                <span>{labels.contact}</span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex rounded-full border border-white/20 bg-black/20 p-1" aria-label={t('language')}>
                {['es', 'en'].map((code) => <button key={code} type="button" onClick={() => setLanguage(code)} aria-pressed={language === code} className={`rounded-full px-2.5 py-1 text-xs font-bold transition ${language === code ? 'bg-purple-500 text-white' : 'text-white/60 hover:text-white'}`}>{code.toUpperCase()}</button>)}
              </div>
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden text-white hover:text-purple-300 transition-colors p-2"
            >
              {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-[#10051c]/95 backdrop-blur-xl z-40 md:hidden">
          <div className="flex flex-col items-center justify-center h-full space-y-6 px-4">
            <button
              onClick={() => handleNavClick('projects')}
              className={`flex items-center space-x-3 text-lg font-medium transition-colors duration-300 ${
                activeSection === 'projects' ? 'text-purple-300' : 'text-white hover:text-purple-300'
              }`}
            >
              <FaGamepad size={24} />
              <span>{labels.projects}</span>
            </button>
            <button
              onClick={() => handleNavClick('portfolio')}
              className={`flex items-center space-x-3 text-lg font-medium transition-colors duration-300 ${
                activeSection === 'portfolio' ? 'text-purple-300' : 'text-white hover:text-purple-300'
              }`}
            >
              <FaImages size={24} />
              <span>{labels.portfolio}</span>
            </button>
            <button
              onClick={() => handleNavClick('services')}
              className={`flex items-center space-x-3 text-lg font-medium transition-colors duration-300 ${
                activeSection === 'services' ? 'text-purple-300' : 'text-white hover:text-purple-300'
              }`}
            >
              <FaBriefcase size={24} />
              <span>{labels.services}</span>
            </button>
            <button
              onClick={() => handleNavClick('members')}
              className={`flex items-center space-x-3 text-lg font-medium transition-colors duration-300 ${
                activeSection === 'members' ? 'text-purple-300' : 'text-white hover:text-purple-300'
              }`}
            >
              <FaUsers size={24} />
              <span>{labels.members}</span>
            </button>
            <button
              onClick={() => handleNavClick('contact')}
              className={`flex items-center space-x-3 text-lg font-medium transition-colors duration-300 ${
                activeSection === 'contact' ? 'text-purple-300' : 'text-white hover:text-purple-300'
              }`}
            >
              <FaEnvelope size={24} />
              <span>{labels.contact}</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;
